import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import Login from './pages/Login';
import Signup from './pages/Signup';
import MagicLinkVerify from './pages/MagicLinkVerify';
import TermsAndConditions from './pages/TermsAndConditions';
import Benefits from './pages/Benefits';
import DubaiRedirect from './pages/DubaiRedirect';
import Catalogue from './pages/Catalogue';
import Dashboard from './pages/Dashboard';
import Referrals from './pages/Referrals';
import Payouts from './pages/Payouts';
import Resources from './pages/Resources';
import Profile from './pages/Profile';
import TierInfo from './pages/TierInfo';
import DashboardLayout from './components/DashboardLayout';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminPartners from './pages/admin/AdminPartners';
import AdminPartnerDetail from './pages/admin/AdminPartnerDetail';
import AdminReferrals from './pages/admin/AdminReferrals';
import AdminPayouts from './pages/admin/AdminPayouts';
import AdminTransactions from './pages/admin/AdminTransactions';
import AdminCRM from './pages/admin/AdminCRM';
import AdminDuplicates from './pages/admin/AdminDuplicates';
import AdminDiagnose from './pages/admin/AdminDiagnose';
import AdminInsiders from './pages/admin/AdminInsiders';
import AdminReengagement from './pages/admin/AdminReengagement';
import KBInsiderLanding from './pages/KBInsiderLanding';
import InsiderMagicLinkVerify from './pages/InsiderMagicLinkVerify';
import InsiderAuthCallback from './pages/InsiderAuthCallback';
import InsiderDashboard from './pages/InsiderDashboard';
import AdminLayout from './components/AdminLayout';
import { Toaster } from 'sonner';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    if (!hash.includes('session_id=')) return;

    const sessionId = hash.split('session_id=')[1]?.split('&')[0];
    if (!sessionId) return;

    const exchangeSession = async () => {
      try {
        const signupLocation = sessionStorage.getItem('signup_location');
        const headers = { 'X-Session-ID': sessionId };
        if (signupLocation) headers['X-Signup-Location'] = signupLocation;
        const response = await fetch(`${API}/auth/session`, {
          method: 'POST',
          headers
        });

        if (!response.ok) throw new Error('Session exchange failed');

        const data = await response.json();
        sessionStorage.removeItem('signup_location'); // Clean up after successful exchange
        
        // Check remember me preference
        const rememberMe = sessionStorage.getItem('remember_me') !== 'false';
        sessionStorage.removeItem('remember_me'); // Clean up
        
        // Store session token based on user preference
        if (rememberMe) {
          localStorage.setItem('session_token', data.session_token);
        } else {
          sessionStorage.setItem('session_token', data.session_token);
        }
        
        // Check T&C status for partners
        if (data.user && data.user.role === 'partner') {
          try {
            const tcResponse = await fetch(`${API}/auth/tc-status`, {
              headers: { 'Authorization': `Bearer ${data.session_token}` }
            });
            
            if (tcResponse.ok) {
              const tcData = await tcResponse.json();
              if (!tcData.tc_accepted) {
                // Redirect to T&C page
                window.history.replaceState(null, '', '/terms-and-conditions');
                navigate('/terms-and-conditions', { replace: true });
                return;
              }
            }
          } catch (tcError) {
            console.error('T&C check error:', tcError);
          }
        }
        
        // Clear the hash from URL and navigate to dashboard
        window.history.replaceState(null, '', '/dashboard');
        navigate('/dashboard', { replace: true, state: { user: data.user } });
      } catch (error) {
        console.error('Auth error:', error);
        window.history.replaceState(null, '', '/login');
        navigate('/login', { replace: true });
      }
    };

    exchangeSession();
  }, [location.hash, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Authenticating...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [tcAccepted, setTcAccepted] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If there's a session_id in the hash, let AuthCallback handle it first
    if (location.hash.includes('session_id=')) {
      return; // Don't check auth, AuthCallback will process the session
    }

    const checkTCStatus = async (userRole) => {
      // Admin doesn't need T&C acceptance
      if (userRole !== 'partner') {
        setTcAccepted(true);
        return;
      }
      
      try {
        const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
        if (!sessionToken) {
          setTcAccepted(false);
          return;
        }
        
        const headers = { 'Authorization': `Bearer ${sessionToken}` };
        const response = await fetch(`${API}/auth/tc-status`, { headers });
        
        if (response.ok) {
          const data = await response.json();
          setTcAccepted(data.tc_accepted);
          if (!data.tc_accepted) {
            navigate('/terms-and-conditions', { replace: true });
          }
        } else {
          setTcAccepted(false);
        }
      } catch (error) {
        console.error('Error checking T&C status:', error);
        setTcAccepted(false);
      }
    };

    const checkAuth = async () => {
      try {
        // Get token from localStorage (persistent) or sessionStorage (session-only)
        const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
        
        if (!sessionToken) {
          setIsAuthenticated(false);
          return;
        }
        
        const headers = { 'Authorization': `Bearer ${sessionToken}` };
        
        const response = await fetch(`${API}/auth/me`, { headers });
        
        if (!response.ok) {
          // Clear invalid tokens from both storages
          localStorage.removeItem('session_token');
          sessionStorage.removeItem('session_token');
          setIsAuthenticated(false);
          return;
        }
        
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
        
        // Check T&C status
        await checkTCStatus(userData.role);
      } catch (error) {
        setIsAuthenticated(false);
        setTcAccepted(null);
        localStorage.removeItem('session_token');
        sessionStorage.removeItem('session_token');
      }
    };

    // Check if user data was passed via location state
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      checkTCStatus(location.state.user.role);
      return;
    }

    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.hash]); // navigate intentionally excluded to prevent loops

  if (isAuthenticated === null || (isAuthenticated && tcAccepted === null)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If T&C not accepted for partners, redirect to T&C page
  if (!tcAccepted && user?.role === 'partner') {
    return <Navigate to="/terms-and-conditions" replace />;
  }

  return children;
}

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/benefits" element={<Benefits />} />
      <Route path="/Dubai" element={<DubaiRedirect />} />
      <Route path="/dubai" element={<DubaiRedirect />} />
      <Route path="/catalogue" element={<Catalogue />} />
      <Route path="/auth/magic-link" element={<MagicLinkVerify />} />
      <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
      <Route path="/admin/login" element={<AdminLogin />} />

      {/* KBInsider (Customer Referral Program) — public + own auth */}
      <Route path="/kbinsider" element={<KBInsiderLanding />} />
      <Route path="/insider/magic-link" element={<InsiderMagicLinkVerify />} />
      <Route path="/insider/auth-callback" element={<InsiderAuthCallback />} />
      <Route path="/insider/dashboard" element={<InsiderDashboard />} />
      
      {/* Partner Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Dashboard />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/referrals"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Referrals />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payouts"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Payouts />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/resources"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Resources />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Profile />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tiers"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <TierInfo />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Admin Routes */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminDashboard />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/partners"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminPartners />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/duplicates"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminDuplicates />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/diagnose"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminDiagnose />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/partners/:partnerId"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminPartnerDetail />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/referrals"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminReferrals />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/payouts"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminPayouts />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/transactions"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminTransactions />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route 
        path="/admin/crm" 
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminCRM />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/insiders"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminInsiders />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reengagement"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminReengagement />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}

export default App;